/**
 * Admin Route Guard
 * Skyddar routes baserat på RBAC-behörigheter
 * is_global_admin har alltid tillgång till allt
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/store/authStore';
import { ROUTES } from '@/constants/routes';
import { usePermissions } from '@/hooks/usePermissions';
import { isSysAdminUser } from '@/utils/isSysAdminUser';

interface AdminRouteProps {
  children: React.ReactNode;
  featureCode?: string | string[];
  /** Kräver explicit action på feature (t.ex. CONFIGURE_DEVICE). Global admin undantas. */
  requiredAction?: string;
  requireSuperAdmin?: boolean;
  requireSysAdmin?: boolean;
  /** Endast rollen SYS_ADMIN — inte is_global_admin och inte RBAC-feature. */
  requireSysAdminRole?: boolean;
}

const AdminRoute = ({
  children,
  featureCode,
  requiredAction,
  requireSuperAdmin = false,
  requireSysAdmin = false,
  requireSysAdminRole = false,
}: AdminRouteProps) => {
  const { user, isAuthenticated } = useAuth();
  const { hasFeatureAccess, hasActionAccess, isLoading, permissions } = usePermissions();

  // Om användaren inte är autentiserad, redirecta till login
  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const isGlobalAdmin = user.is_global_admin === true || user.is_global_admin === 1 || user.is_global_admin === '1';
  const roleCodes = Array.isArray(user.roleCodes) ? user.roleCodes : [];
  const isOrgAdmin = roleCodes.includes('ADMIN');

  if (requireSysAdminRole) {
    if (!isSysAdminUser(user)) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
    return <>{children}</>;
  }

  // Global admin har alltid tillgång till allt
  if (isGlobalAdmin) {
    return <>{children}</>;
  }

  // Om RBAC featureCode anges, kolla mot permissions
  if (featureCode) {
    // Vänta på att permissions laddas (isLoading ELLER permissions inte hämtade ännu)
    if (isLoading || !permissions) {
      return <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bgPrimary"></div>
      </div>;
    }

    const codes = Array.isArray(featureCode) ? featureCode : [featureCode];
    const hasFeature = codes.some((code) => hasFeatureAccess(code));
    if (!hasFeature) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }

    if (requiredAction) {
      const hasAction = codes.some((code) => hasActionAccess(code, requiredAction));
      if (!hasAction) {
        return <Navigate to={ROUTES.DASHBOARD} replace />;
      }
    }

    return <>{children}</>;
  }

  // Om SysAdmin krävs (= global admin)
  if (requireSysAdmin && !isGlobalAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Om SuperAdmin krävs (= global admin)
  if (requireSuperAdmin && !isGlobalAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // För vanlig admin utan specifik feature, tillåt org-admin (RBAC ADMIN)
  if (!featureCode && !requireSuperAdmin && !requireSysAdmin && !requireSysAdminRole) {
    if (isOrgAdmin) {
      return <>{children}</>;
    }
  }

  return <Navigate to={ROUTES.DASHBOARD} replace />;
};

export default AdminRoute;
